import {
  MANUAL_CONFIRM_TAG,
  ORDER_STAGE_LABELS,
  canMarkConfirmed,
  type FieldDefinition,
  type Order,
  type OrderStage,
  type PackingSlipFlag,
} from "@estelle/domain";
import { EscalatedLabel } from "@/modules/orders/screens/EscalatedLabel";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Printer } from "lucide-react";
import { useEffect, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { can, usePermissions } from "@/auth/permissions";
import { BulkProgress, BulkReport } from "@/components/table/BulkReport";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { SelectionBar } from "@/components/table/SelectionBar";
import {
  SELECT_ALL_CAP,
  inViewOf,
  isTyping,
  previewNames,
  selectionFacts,
  stepRow,
  useSelection,
} from "@/components/table/selection";
import { useBulkRun } from "@/components/table/useBulkRun";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import { setFieldCondition } from "@/components/filters/tree";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { dayGroupLabel, itemsLabel } from "@/modules/orders/format";
import { EMPHASIS_CLASS, orderEmphasis } from "@/modules/orders/emphasis";
import { projectOrder } from "@/modules/orders/projections";
import { ChangeStageMenu } from "@/modules/orders/screens/ChangeStageMenu";
import { SelectByNumbers } from "@/modules/orders/screens/SelectByNumbers";
import { OrderDetail } from "@/modules/orders/screens/OrderDetail";
import {
  AmountCell,
  CustomerCell,
  FulfilmentStatusPill,
  OrderMobileCard,
  OrderNumberCell,
  OrderQuickIcons,
  PaymentStatusPill,
} from "@/modules/orders/screens/OrderRowParts";
import {
  ConfirmationLabel,
  StageCell,
  StageLegendButton,
} from "@/modules/orders/screens/StageParts";
import {
  HealthLabel,
  StageBadge,
} from "@/modules/orders/screens/TrackingParts";
import {
  exactIndiaTime,
  needsAttention,
  timeAgo,
} from "@/modules/orders/tracking";
import {
  withStanding,
  type StandingCondition,
} from "@/modules/orders/standing";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * The Orders list, and the three sections that are the same list narrowed.
 *
 * `baseFilter` is applied on top of whatever the user has built rather than
 * replacing it, so Unfulfilled stays unfulfilled while someone filters within
 * it. A section that overwrote the user's filter would silently discard work
 * the moment they changed section.
 */
export function ordersList(baseFilter?: StandingCondition) {
  return function OrdersList({
    fields,
    filter,
    search,
    sort,
    onToggleSort,
    onClearFilter,
    onChangeFilter,
  }: SectionScreenProps) {
    const data = useDataSource();
    const permissions = usePermissions();
    const queryClient = useQueryClient();
    const canEditTags = can(permissions, "orders.detail.editTags");
    const canPrint = can(permissions, "orders.list.export");
    const canOverrideStage = can(permissions, "orders.stage.override");

    /**
     * The ticked orders (#634), kept across pages and filter changes: pick
     * five from five different filters, then act once.
     */
    const selection = useSelection<Order>((order) => order.id);
    const [bulkNote, setBulkNote] = useState<string | null>(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const markConfirmed = useBulkRun<Order>((order) =>
      data.orders.addTag(order.id, MANUAL_CONFIRM_TAG, {
        expectedUpdatedAt: order.channelUpdatedAt,
      }),
    );
    /** The last run's outcomes the selection has been trimmed to. */
    const [settled, setSettled] = useState(markConfirmed.outcomes);
    /** Orders the packing slips were refused for until asked again (#596). */
    const [slipFlags, setSlipFlags] = useState<PackingSlipFlag[] | null>(null);
    /** The paste-order-numbers dialog (#621). */
    const [pasting, setPasting] = useState(false);

    const [limit, setLimit] = useState(25);
    /**
     * Paging stays local rather than joining filter, columns and sort in the
     * URL. A shared link should reproduce what someone was looking at, and
     * page four of a list that has since gained rows is not that.
     */
    const [history, setHistory] = useCursorHistory();
    const cursor = history[history.length - 1];
    /**
     * Which order is open lives in the URL (M2-11), so the record can be
     * refreshed, linked and shared, and closing it returns to this exact
     * filtered list rather than to an unfiltered one.
     */
    const { recordId, open } = useOpenRecord();

    const effective = withStanding(baseFilter, filter);

    const query = useQuery({
      queryKey: ["orders", "list", effective, search, sort, limit, cursor],
      queryFn: () =>
        data.orders.list({ filter: effective, search, sort, limit, cursor }),
    });

    /*
      With an order open, the arrow keys step through the rows beside it
      (#644), so a queue is worked without closing the panel. Not while typing
      in a field, where the arrows move the caret.
    */
    const pageIds = (
      selection.viewing
        ? [...selection.rows.values()]
        : (query.data?.items ?? [])
    ).map((order) => order.id);
    const pageKey = pageIds.join(",");
    useEffect(() => {
      if (recordId === null) return;
      const ids = pageKey.split(",");
      function onKey(event: KeyboardEvent) {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        if (isTyping(event.target) || recordId === null) return;
        const next = stepRow(ids, recordId, event.key === "ArrowDown" ? 1 : -1);
        if (next) {
          event.preventDefault();
          open(next);
        }
      }
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
      };
    }, [recordId, pageKey, open]);

    // The desktop pill reuses `fields.ts`'s own label for the value — "Pending",
    // not the mobile card's "Payment pending" — so the column and the filter
    // builder never describe the same state two ways.
    const financialStatusField = fields.find(
      (field) => field.name === "financialStatus",
    );
    const fulfilmentStatusField = fields.find(
      (field) => field.name === "fulfilmentStatus",
    );

    function renderField(field: FieldDefinition, order: Order) {
      switch (field.name) {
        case "orderNumber":
          return <OrderNumberCell order={order} />;
        case "stage":
          return (
            <StageCell
              stage={order.stage}
              overdueHours={order.overdueHours}
              override={order.stageOverride}
            />
          );
        case "confirmation":
          return order.confirmation ? (
            <ConfirmationLabel outcome={order.confirmation} />
          ) : (
            <span className="text-muted-foreground text-xs">
              {order.paymentGatewayKey === "cod" ? "—" : "Prepaid, no call"}
            </span>
          );
        case "placedAt":
          // "5 hours ago", the exact time on hover (#643): which orders are
          // old is read off the column, not worked out from dates.
          return (
            <span title={exactIndiaTime(order.placedAt)}>
              {timeAgo(order.placedAt, reportingNow())}
            </span>
          );
        case "customerName":
          return <CustomerCell order={order} />;
        case "total":
          return <AmountCell order={order} />;
        case "itemCount":
          return itemsLabel(order.itemCount);
        case "financialStatus": {
          const status = order.financialStatus;
          if (status === null) {
            return <span className="text-muted-foreground">—</span>;
          }
          const label =
            financialStatusField?.options?.find(
              (option) => option.value === status,
            )?.label ?? status;
          return <PaymentStatusPill status={status} label={label} />;
        }
        case "fulfilmentStatus": {
          // Unfulfilled is `null` on the order, not a fourth enum member —
          // same "unfulfilled" key the mobile card's own label map uses, so
          // the two never colour or word that state differently (#357).
          const status = order.fulfilmentStatus;
          const key = status ?? "unfulfilled";
          const label = status
            ? (fulfilmentStatusField?.options?.find(
                (option) => option.value === status,
              )?.label ?? status)
            : "Unfulfilled";
          return <FulfilmentStatusPill status={key} label={label} />;
        }
        case "deliveryStatus": {
          // Tracking's health, only where a parcel needs a look (#527): next to
          // the status it qualifies, and wrapping under it in a narrow column,
          // so a flagged row grows and no column does. A parcel that is on
          // track says nothing here — the Tracking health column is for that.
          if (!needsAttention(order.shipmentHealth) && !order.escalated) {
            return undefined;
          }
          return (
            <span className="inline-flex flex-wrap items-center gap-1">
              {order.deliveryStatus
                ? renderCell(field, projectOrder(order).deliveryStatus)
                : null}
              {needsAttention(order.shipmentHealth) ? (
                <HealthLabel health={order.shipmentHealth} />
              ) : null}
              {order.escalated ? <EscalatedLabel /> : null}
            </span>
          );
        }
        // Chosen from the column chooser: the ERP's own stage and health, from
        // the one shipment that speaks for the order.
        case "shipmentStage":
          return order.shipmentStage ? (
            <StageBadge stage={order.shipmentStage} />
          ) : undefined;
        case "shipmentHealth":
          return order.shipmentHealth ? (
            <HealthLabel health={order.shipmentHealth} />
          ) : undefined;
        default:
          return undefined;
      }
    }

    /**
     * The quick view's icon (#489) goes in whichever column leads, not in the
     * order-number column specifically: that column can be hidden in the
     * chooser, and the icon would go with it.
     */
    function renderOverride(field: FieldDefinition, order: Order) {
      const cell = renderField(field, order);
      if (field.name !== fields[0]?.name) return cell;
      return (
        <span className="inline-flex items-center gap-1">
          {cell ?? renderCell(field, projectOrder(order)[field.name])}
          <OrderQuickIcons order={order} className="-my-1" />
        </span>
      );
    }

    /** Choosing a stage in the legend shows those orders (#643). */
    function filterByStage(stage: OrderStage) {
      onChangeFilter(
        setFieldCondition(filter, "stage", {
          kind: "condition",
          field: "stage",
          operator: "is_any_of",
          value: [stage],
        }),
      );
    }

    // Any order can have a packing slip or a stage change; only some can be
    // marked confirmed. No action open to this person, no boxes at all.
    const anyAction = canEditTags || canPrint || canOverrideStage;
    const selectable = (order: Order) =>
      canPrint ||
      canOverrideStage ||
      (canEditTags &&
        canMarkConfirmed(order.stage, order.paymentGatewayKey === "cod"));

    if (query.isPending) return <TableSkeleton />;

    if (query.isError) {
      return (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    }

    const pageOrders = query.data.items;
    // Actions act on every selected order, on this page or not (#634).
    const picked = [...selection.rows.values()].slice(0, SELECT_ALL_CAP);
    const confirmable = canEditTags
      ? picked.filter((order) =>
          canMarkConfirmed(order.stage, order.paymentGatewayKey === "cod"),
        )
      : [];
    const facts = selectionFacts({
      selection,
      pageRows: pageOrders,
      idOf: (order) => order.id,
      selectable,
      inView: inViewOf(projectOrder, effective, reportingNow()),
    });

    /** "Select all 78 that match": the filter's orders, up to the cap. */
    async function selectAllMatching() {
      const found: Order[] = [];
      let next: string | undefined;
      do {
        const page = await data.orders.list({
          filter: effective,
          search,
          sort,
          limit: 100,
          cursor: next,
        });
        found.push(...page.items.filter(selectable));
        next = page.nextCursor ?? undefined;
      } while (next && found.length < SELECT_ALL_CAP);
      selection.setMany(found.slice(0, SELECT_ALL_CAP), true);
    }

    /**
     * Orders found from pasted numbers (#621): ticked like any others, across
     * pages and filters, and the numbers that matched nothing are said, not
     * dropped. Orders this person cannot act on are counted, not ticked.
     */
    function selectFound(found: Order[], missing: string[]) {
      const usable = found.filter(selectable);
      selection.setMany(usable, true);
      const notes = [
        `Selected ${String(usable.length)} ${usable.length === 1 ? "order" : "orders"}.`,
      ];
      if (usable.length < found.length) {
        notes.push(
          `${String(found.length - usable.length)} cannot be acted on by you.`,
        );
      }
      if (missing.length > 0) {
        notes.push(`Not found: ${missing.join(", ")}.`);
      }
      setBulkNote(notes.join(" "));
    }

    /**
     * Packing slips (#596): one PDF for the ticked orders. Flagged ones are
     * asked about first, never printed or dropped without a choice.
     */
    async function printSlips(flagged?: "include" | "skip") {
      setBulkBusy(true);
      setBulkNote(null);
      try {
        const result = await data.orders.packingSlips(
          picked.map((order) => order.id),
          flagged,
        );
        if (result.kind === "flagged") {
          setSlipFlags(result.flagged);
          return;
        }
        setSlipFlags(null);
        if (result.kind === "refused") {
          setBulkNote(result.message);
          return;
        }
        const url = URL.createObjectURL(
          new Blob([result.pdf], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = "packing-slips.pdf";
        link.click();
        URL.revokeObjectURL(url);
        selection.clear();
        setBulkNote(`Packing slips ready for ${String(picked.length)} orders.`);
      } finally {
        setBulkBusy(false);
      }
    }

    /**
     * Mark confirmed (#634): a few at a time with progress, each its own write
     * to Shopify, refused or accepted on its own. What went through leaves the
     * selection; what failed stays, with its reason and a retry.
     */
    async function markSelectedConfirmed(retry = false) {
      setConfirming(false);
      setBulkNote(null);
      if (retry) await markConfirmed.retryFailed();
      else await markConfirmed.run(confirmable);
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    }

    // After a run: drop what went through from the selection, keep the rest.
    const outcomes = markConfirmed.outcomes;
    if (outcomes !== settled) {
      setSettled(outcomes);
      if (outcomes) {
        selection.keepOnly(
          new Set(
            outcomes
              .filter((outcome) => outcome.status !== "success")
              .map((outcome) => outcome.item.id),
          ),
        );
      }
    }

    const shownOrders = selection.viewing
      ? [...selection.rows.values()]
      : pageOrders;

    const actions = (
      <>
        {confirmable.length > 0 ? (
          <Button
            size="sm"
            className="max-md:min-h-11"
            disabled={bulkBusy || markConfirmed.running}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {confirmable.length === picked.length
              ? "Mark confirmed"
              : `Mark confirmed (${String(confirmable.length)})`}
          </Button>
        ) : null}
        {canPrint ? (
          <Button
            size="sm"
            variant="outline"
            className="max-md:min-h-11"
            disabled={bulkBusy || markConfirmed.running}
            onClick={() => {
              void printSlips();
            }}
          >
            <Printer />
            Print packing slips
          </Button>
        ) : null}
        {canOverrideStage ? (
          <ChangeStageMenu
            orderIds={picked.map((order) => order.id)}
            onDone={(outcome, stage) => {
              selection.clear();
              const what =
                stage === null
                  ? "back to automatic"
                  : `set to ${ORDER_STAGE_LABELS[stage]}`;
              setBulkNote(
                `${String(outcome.changed)} ${outcome.changed === 1 ? "order" : "orders"} ${what}.${
                  outcome.skipped > 0
                    ? ` ${String(outcome.skipped)} skipped (cancelled, return received, or already so).`
                    : ""
                }`,
              );
            }}
          />
        ) : null}
      </>
    );

    const note = markConfirmed.progress ? (
      <BulkProgress progress={markConfirmed.progress} />
    ) : outcomes ? (
      <BulkReport
        outcomes={outcomes}
        labelOf={(order) => order.orderNumber}
        verb="marked confirmed"
        retrying={markConfirmed.running}
        onRetry={() => {
          void markSelectedConfirmed(true);
        }}
      />
    ) : bulkNote ? (
      <p className="text-sm">{bulkNote}</p>
    ) : null;

    return (
      <div className="flex h-full min-h-0 flex-col">
        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent
            title={`Mark ${String(confirmable.length)} ${confirmable.length === 1 ? "order" : "orders"} confirmed?`}
            description={`Adds the “${MANUAL_CONFIRM_TAG}” tag in Shopify to each, a few at a time. One that Shopify refuses changes nothing.`}
          >
            <p className="text-sm">
              {previewNames(confirmable.map((order) => order.orderNumber))}
            </p>
            {confirmable.length < picked.length ? (
              <p className="text-muted-foreground text-sm">
                {picked.length - confirmable.length} of the selected orders are
                past confirmation and are left as they are.
              </p>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  void markSelectedConfirmed();
                }}
              >
                Mark {confirmable.length} confirmed
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={slipFlags !== null}
          onOpenChange={(next) => {
            if (!next && !bulkBusy) setSlipFlags(null);
          }}
        >
          <DialogContent
            title="Some orders may not need packing"
            description="These are cancelled, already fulfilled, or have a cancel request."
          >
            <ul className="space-y-1 text-sm">
              {(slipFlags ?? []).map((flag) => (
                <li key={flag.id}>
                  <b>{flag.orderNumber}</b>{" "}
                  <span className="text-muted-foreground">
                    {flag.reasons.join(", ")}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                disabled={bulkBusy}
                onClick={() => {
                  setSlipFlags(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                disabled={bulkBusy}
                onClick={() => {
                  void printSlips("skip");
                }}
              >
                Print without them
              </Button>
              <Button
                disabled={bulkBusy}
                onClick={() => {
                  void printSlips("include");
                }}
              >
                Print all
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <SelectByNumbers
          open={pasting}
          onOpenChange={setPasting}
          onFound={selectFound}
        />
        {canPrint || canOverrideStage ? (
          <div className="flex shrink-0 justify-end border-b px-3 py-1 sm:px-6">
            <Button
              variant="ghost"
              size="sm"
              className="max-md:min-h-11"
              onClick={() => {
                setPasting(true);
              }}
            >
              <ListChecks />
              Select by order numbers
            </Button>
          </div>
        ) : null}
        <DataTable
          fields={fields}
          rows={shownOrders}
          getRowId={(order) => order.id}
          project={projectOrder}
          sort={sort}
          onToggleSort={onToggleSort}
          onRowClick={(order) => {
            open(order.id);
          }}
          renderOverride={renderOverride}
          selection={
            anyAction
              ? {
                  isSelected: (order) => selection.has(order.id),
                  onToggle: (order, on) => {
                    setBulkNote(null);
                    selection.set(order, on);
                  },
                  onTogglePage: (orders, on) => {
                    setBulkNote(null);
                    selection.setMany(orders, on);
                  },
                  selectable,
                  labelOf: (order) => `Select ${order.orderNumber}`,
                  active: selection.size > 0,
                }
              : undefined
          }
          mobileRow={(order) => (
            <OrderMobileCard
              order={order}
              // The card ignores the column chooser, but not this one: asked
              // for, the stage is on it (#527).
              showStage={fields.some((field) => field.name === "shipmentStage")}
            />
          )}
          // Show the exception, mute the normal (#643): an edge and bold type
          // on a row that needs action, quiet grey on one that is finished.
          // Cancelled orders stay listed but read as cancelled at a glance.
          rowClassName={(order) => {
            const emphasis = orderEmphasis(order);
            return cn(
              emphasis ? EMPHASIS_CLASS[emphasis] : undefined,
              order.cancelledAt && "line-through",
            );
          }}
          headerExtra={(field) =>
            field.name === "stage" ? (
              <StageLegendButton onPick={filterByStage} />
            ) : null
          }
          mobileGroupBy={(order) => dayGroupLabel(order.placedAt)}
          emptyState={<NoMatches onClear={onClearFilter} />}
          footer={
            // Viewing the selection shows every chosen order at once: there
            // is no page of it to turn.
            selection.viewing ? undefined : (
              <Pagination
                page={query.data}
                limit={limit}
                onLimitChange={(next) => {
                  setLimit(next);
                  setHistory([undefined]);
                }}
                history={history}
                onNavigate={setHistory}
              />
            )
          }
        />

        <SelectionBar
          selection={selection}
          onPage={facts.onPage}
          notInView={facts.notInView}
          pageFullySelected={facts.pageFullySelected}
          matching={query.data.total}
          onSelectAllMatching={selectAllMatching}
          noun="orders"
          actions={actions}
          note={note}
        />

        {/*
          Rendered beside the table rather than instead of it, so closing the
          drawer costs no refetch and lands on the same page of the same list.

          The title comes from the row where the list is holding it. A link
          opened cold lands on a record this page does not contain, so it
          falls back to the generic name until `OrderDetail` has fetched —
          the alternative, blocking the drawer on its own lookup, would show
          an empty panel for as long as the fetch takes.
        */}
        {recordId !== null ? (
          <RecordDrawer
            wide
            title={
              query.data.items.find((order) => order.id === recordId)
                ?.orderNumber ?? "Order"
            }
          >
            <OrderDetail orderId={recordId} />
          </RecordDrawer>
        ) : null}
      </div>
    );
  };
}
